package org.teddapp.project.card

/**
 * Code QR, calculé dans l'application : la carte de visite se scanne sans réseau.
 *
 * Mode octets (le texte part en UTF-8), correction d'erreur moyenne (M), versions 1 à 20,
 * soit jusqu'à 666 octets : une vCard tient largement. Au-delà, [encode] rend `null`.
 */
object QrCode {
    /** Par version : octets de correction par bloc, puis nombre de blocs. Niveau M. */
    private val EC_PER_BLOCK = intArrayOf(0, 10, 16, 26, 18, 24, 16, 18, 22, 22, 26, 30, 22, 22, 24, 24, 28, 28, 26, 26, 26)
    private val BLOCKS = intArrayOf(0, 1, 1, 1, 2, 2, 4, 4, 4, 5, 5, 5, 8, 9, 9, 10, 10, 11, 13, 14, 16)
    const val MAX_VERSION = 20

    /** Les modules du code, `true` pour un module sombre. `null` si le texte est trop long. */
    fun encode(text: String): Array<BooleanArray>? {
        val data = text.encodeToByteArray()
        val version = (1..MAX_VERSION).firstOrNull { v -> 4 + lengthBits(v) + data.size * 8 <= dataCodewords(v) * 8 } ?: return null
        val codewords = interleave(version, dataBytes(version, data))
        val size = version * 4 + 17
        val modules = Array(size) { BooleanArray(size) }
        val reserved = Array(size) { BooleanArray(size) }
        drawFunctionPatterns(version, modules, reserved)
        drawCodewords(codewords, modules, reserved)
        // Le masque qui donne le motif le plus lisible, d'après la pénalité de la norme.
        var best = 0
        var bestPenalty = Int.MAX_VALUE
        for (mask in 0..7) {
            applyMask(mask, modules, reserved)
            drawFormatBits(mask, modules, reserved)
            val p = penalty(modules)
            if (p < bestPenalty) { bestPenalty = p; best = mask }
            applyMask(mask, modules, reserved)
        }
        applyMask(best, modules, reserved)
        drawFormatBits(best, modules, reserved)
        return modules
    }

    private fun lengthBits(version: Int) = if (version <= 9) 8 else 16

    /** Modules disponibles pour les données et la correction, hors motifs fixes. */
    private fun rawModules(version: Int): Int {
        var result = (16 * version + 128) * version + 64
        if (version >= 2) {
            val align = version / 7 + 2
            result -= (25 * align - 10) * align - 55
            if (version >= 7) result -= 36
        }
        return result
    }

    internal fun totalCodewords(version: Int) = rawModules(version) / 8
    internal fun dataCodewords(version: Int) = totalCodewords(version) - EC_PER_BLOCK[version] * BLOCKS[version]

    /** En-tête, octets du texte, terminaison et bourrage jusqu'à la capacité de la version. */
    private fun dataBytes(version: Int, data: ByteArray): IntArray {
        val capacity = dataCodewords(version)
        val bits = ArrayList<Boolean>(capacity * 8)
        fun append(value: Int, count: Int) { for (i in count - 1 downTo 0) bits.add((value ushr i) and 1 == 1) }
        append(0b0100, 4)
        append(data.size, lengthBits(version))
        for (b in data) append(b.toInt() and 0xFF, 8)
        append(0, minOf(4, capacity * 8 - bits.size))
        append(0, (8 - bits.size % 8) % 8)
        var pad = 0xEC
        while (bits.size < capacity * 8) { append(pad, 8); pad = pad xor (0xEC xor 0x11) }
        return IntArray(capacity) { i -> (0 until 8).fold(0) { acc, j -> (acc shl 1) or (if (bits[i * 8 + j]) 1 else 0) } }
    }

    /** Découpe en blocs, ajoute la correction à chacun, puis entrelace les blocs. */
    private fun interleave(version: Int, data: IntArray): IntArray {
        val blocks = BLOCKS[version]
        val ecLen = EC_PER_BLOCK[version]
        val total = totalCodewords(version)
        val shortBlocks = blocks - total % blocks
        val shortLen = total / blocks - ecLen
        val divisor = rsDivisor(ecLen)
        val dataBlocks = ArrayList<IntArray>()
        val ecBlocks = ArrayList<IntArray>()
        var k = 0
        for (i in 0 until blocks) {
            val len = shortLen + if (i < shortBlocks) 0 else 1
            val block = data.copyOfRange(k, k + len)
            k += len
            dataBlocks.add(block)
            ecBlocks.add(rsRemainder(block, divisor))
        }
        val out = ArrayList<Int>(total)
        for (i in 0..shortLen) for (b in dataBlocks) if (i < b.size) out.add(b[i])
        for (i in 0 until ecLen) for (b in ecBlocks) out.add(b[i])
        return out.toIntArray()
    }

    // ── Reed-Solomon sur GF(2^8), polynôme 0x11D ──
    private fun gfMul(x: Int, y: Int): Int {
        var z = 0
        for (i in 7 downTo 0) {
            z = (z shl 1) xor ((z ushr 7) * 0x11D)
            z = z xor (((y ushr i) and 1) * x)
        }
        return z
    }

    private fun rsDivisor(degree: Int): IntArray {
        val result = IntArray(degree)
        result[degree - 1] = 1
        var root = 1
        repeat(degree) {
            for (j in 0 until degree) {
                result[j] = gfMul(result[j], root)
                if (j + 1 < degree) result[j] = result[j] xor result[j + 1]
            }
            root = gfMul(root, 0x02)
        }
        return result
    }

    private fun rsRemainder(data: IntArray, divisor: IntArray): IntArray {
        val result = IntArray(divisor.size)
        for (b in data) {
            val factor = b xor result[0]
            for (i in 0 until result.size - 1) result[i] = result[i + 1]
            result[result.size - 1] = 0
            for (i in result.indices) result[i] = result[i] xor gfMul(divisor[i], factor)
        }
        return result
    }

    // ── Motifs fixes ──
    private fun alignmentPositions(version: Int): IntArray {
        if (version == 1) return IntArray(0)
        val count = version / 7 + 2
        val step = (version * 4 + count * 2 + 1) / (count * 2 - 2) * 2
        val result = IntArray(count)
        result[0] = 6
        var pos = version * 4 + 10
        for (i in count - 1 downTo 1) { result[i] = pos; pos -= step }
        return result
    }

    private fun drawFunctionPatterns(version: Int, m: Array<BooleanArray>, r: Array<BooleanArray>) {
        val size = m.size
        fun set(x: Int, y: Int, dark: Boolean) { m[y][x] = dark; r[y][x] = true }
        for (i in 0 until size) { set(6, i, i % 2 == 0); set(i, 6, i % 2 == 0) }
        fun finder(cx: Int, cy: Int) {
            for (dy in -4..4) for (dx in -4..4) {
                val x = cx + dx
                val y = cy + dy
                if (x in 0 until size && y in 0 until size) {
                    val dist = maxOf(kotlin.math.abs(dx), kotlin.math.abs(dy))
                    set(x, y, dist != 2 && dist != 4)
                }
            }
        }
        finder(3, 3); finder(size - 4, 3); finder(3, size - 4)
        val align = alignmentPositions(version)
        for (i in align.indices) for (j in align.indices) {
            val corner = (i == 0 && j == 0) || (i == 0 && j == align.size - 1) || (i == align.size - 1 && j == 0)
            if (!corner) for (dy in -2..2) for (dx in -2..2) {
                set(align[i] + dx, align[j] + dy, maxOf(kotlin.math.abs(dx), kotlin.math.abs(dy)) != 1)
            }
        }
        // Les emplacements du format sont réservés ici, remplis au choix du masque.
        drawFormatBits(0, m, r)
        if (version >= 7) {
            var rem = version
            repeat(12) { rem = (rem shl 1) xor ((rem ushr 11) * 0x1F25) }
            val bits = (version shl 12) or rem
            for (i in 0 until 18) {
                val bit = (bits ushr i) and 1 == 1
                val a = size - 11 + i % 3
                val b = i / 3
                set(a, b, bit); set(b, a, bit)
            }
        }
    }

    private fun drawFormatBits(mask: Int, m: Array<BooleanArray>, r: Array<BooleanArray>) {
        val size = m.size
        // Niveau M : bits 00.
        val data = mask
        var rem = data
        repeat(10) { rem = (rem shl 1) xor ((rem ushr 9) * 0x537) }
        val bits = ((data shl 10) or rem) xor 0x5412
        fun bit(i: Int) = (bits ushr i) and 1 == 1
        fun set(x: Int, y: Int, dark: Boolean) { m[y][x] = dark; r[y][x] = true }
        for (i in 0..5) set(8, i, bit(i))
        set(8, 7, bit(6)); set(8, 8, bit(7)); set(7, 8, bit(8))
        for (i in 9 until 15) set(14 - i, 8, bit(i))
        for (i in 0 until 8) set(size - 1 - i, 8, bit(i))
        for (i in 8 until 15) set(8, size - 15 + i, bit(i))
        set(8, size - 8, true)
    }

    /** Pose les bits en zigzag, de bas en haut puis de haut en bas, deux colonnes à la fois. */
    private fun drawCodewords(data: IntArray, m: Array<BooleanArray>, r: Array<BooleanArray>) {
        val size = m.size
        var i = 0
        var right = size - 1
        while (right >= 1) {
            if (right == 6) right = 5
            for (vert in 0 until size) {
                for (j in 0..1) {
                    val x = right - j
                    val upward = ((right + 1) and 2) == 0
                    val y = if (upward) size - 1 - vert else vert
                    if (!r[y][x] && i < data.size * 8) {
                        m[y][x] = (data[i ushr 3] ushr (7 - (i and 7))) and 1 == 1
                        i++
                    }
                }
            }
            right -= 2
        }
    }

    private fun applyMask(mask: Int, m: Array<BooleanArray>, r: Array<BooleanArray>) {
        for (y in m.indices) for (x in m.indices) {
            val invert = when (mask) {
                0 -> (x + y) % 2 == 0
                1 -> y % 2 == 0
                2 -> x % 3 == 0
                3 -> (x + y) % 3 == 0
                4 -> (x / 3 + y / 2) % 2 == 0
                5 -> x * y % 2 + x * y % 3 == 0
                6 -> (x * y % 2 + x * y % 3) % 2 == 0
                else -> ((x + y) % 2 + x * y % 3) % 2 == 0
            }
            if (invert && !r[y][x]) m[y][x] = !m[y][x]
        }
    }

    /** Pénalité de la norme : suites, pavés, faux motifs de repérage, déséquilibre clair/sombre. */
    private fun penalty(m: Array<BooleanArray>): Int {
        val size = m.size
        var result = 0
        fun lines(get: (Int, Int) -> Boolean) {
            for (a in 0 until size) {
                var run = 1
                for (b in 1 until size) {
                    if (get(a, b) == get(a, b - 1)) {
                        run++
                        if (run == 5) result += 3 else if (run > 5) result++
                    } else run = 1
                }
                // Motif 1:1:3:1:1 bordé de clair, dans les deux sens.
                for (b in 0..size - 11) {
                    val p = BooleanArray(11) { get(a, b + it) }
                    val fwd = p[0] && !p[1] && p[2] && p[3] && p[4] && !p[5] && p[6] && !p[7] && !p[8] && !p[9] && !p[10]
                    val back = !p[0] && !p[1] && !p[2] && !p[3] && p[4] && !p[5] && p[6] && p[7] && p[8] && !p[9] && p[10]
                    if (fwd || back) result += 40
                }
            }
        }
        lines { a, b -> m[a][b] }
        lines { a, b -> m[b][a] }
        for (y in 0 until size - 1) for (x in 0 until size - 1) {
            val c = m[y][x]
            if (c == m[y][x + 1] && c == m[y + 1][x] && c == m[y + 1][x + 1]) result += 3
        }
        val dark = m.sumOf { row -> row.count { it } }
        val total = size * size
        result += (kotlin.math.abs(dark * 20 - total * 10) + total - 1) / total * 10 - 10
        return maxOf(result, 0)
    }
}
